/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { useState, useCallback } from "react";

export type SortOrder = "asc" | "desc" | null;

interface UseSortReturn {
  sortBy: string;
  sortOrder: SortOrder;
  handleSortChange: (newSortBy: string, newSortOrder: SortOrder) => void;
  buildSortParam: () => string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>;
}

/**
 * Custom hook for managing sort state in data tables.
 * Provides state management for sortBy and sortOrder, along with helper functions.
 * 
 * @param initialSortBy - Initial field to sort by (default: "")
 * @param initialSortOrder - Initial sort order (default: null)
 * @returns Object containing sort state and helper functions
 * 
 * @example
 * const { sortBy, sortOrder, handleSortChange, buildSortParam } = useSort();
 * 
 * // In useEffect for API call:
 * const url = `${CoreAPI_URLs.ENDPOINT}?${buildSortParam()}page=${page}&pageSize=${pageSize}`;
 * 
 * // Pass to table component:
 * <DataTable onSortChange={handleSortChange} currentSortBy={sortBy} currentSortOrder={sortOrder} />
 */
const useSort = (
  initialSortBy: string = "",
  initialSortOrder: SortOrder = null
): UseSortReturn => {
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);

  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: SortOrder) => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
    },
    []
  );

  const buildSortParam = useCallback((): string => {
    return sortBy
      ? `sortBy=${encodeURIComponent(sortBy)}${sortOrder ? `&sortOrder=${sortOrder}` : ""}&`
      : "";
  }, [sortBy, sortOrder]);

  return {
    sortBy,
    sortOrder,
    handleSortChange,
    buildSortParam,
    setSortBy,
    setSortOrder,
  };
};

export default useSort;
